import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

const TOKEN_KEY = "eu_glossary_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); };

// Attach bearer token to every request
api.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Auto-logout on 401
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && getToken()) {
      setToken(null);
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

// Auth
export const login = (email, password) =>
  api.post("/auth/login", { email, password }).then((r) => r.data);
export const logoutApi = () => api.post("/auth/logout").then((r) => r.data).catch(() => ({ ok: true }));
export const fetchMe = () => api.get("/auth/me").then((r) => r.data);

// Users (admin)
export const listUsers = () => api.get("/users").then((r) => r.data);
export const createUser = (payload) => api.post("/users", payload).then((r) => r.data);
export const updateUser = (id, payload) => api.put(`/users/${id}`, payload).then((r) => r.data);
export const deleteUser = (id) => api.delete(`/users/${id}`).then((r) => r.data);

// Ingredients
export const listIngredients = (params) => api.get("/ingredients", { params }).then((r) => r.data);
export const getStats = () => api.get("/ingredients/stats").then((r) => r.data);
export const getIngredient = (id) => api.get(`/ingredients/${id}`).then((r) => r.data);
export const createIngredient = (payload) => api.post("/ingredients", payload).then((r) => r.data);
export const updateIngredient = (id, payload) => api.put(`/ingredients/${id}`, payload).then((r) => r.data);
export const deleteIngredient = (id) => api.delete(`/ingredients/${id}`).then((r) => r.data);

export const uploadDoc = (id, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/ingredients/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const deleteDoc = (id, docId) => api.delete(`/ingredients/${id}/documents/${docId}`).then((r) => r.data);

// Downloads that must include the bearer token: fetch as blob and open
async function openAuthedDownload(url, filename) {
  const res = await api.get(url.replace(API, ""), { responseType: "blob" });
  const blob = new Blob([res.data], { type: res.headers["content-type"] || "application/octet-stream" });
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  if (filename) a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
}

export const downloadIngredientDoc = (id, docId, filename) =>
  openAuthedDownload(`${API}/ingredients/${id}/documents/${docId}`, filename);

export const downloadExport = (url, filename) => openAuthedDownload(url, filename);

export const exportUrls = (id) => ({
  json: `${API}/ingredients/${id}/export/json`,
  pdf: `${API}/ingredients/${id}/export/pdf`,
  cpsr: `${API}/ingredients/${id}/export/cpsr`,
});

// Versions
export const listVersions = (id) => api.get(`/ingredients/${id}/versions`).then((r) => r.data);
export const restoreVersion = (id, vid) => api.post(`/ingredients/${id}/versions/${vid}/restore`).then((r) => r.data);

// CSV
export const csvTemplateUrl = `${API}/ingredients/import/template`;
export const importCsv = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post(`/ingredients/import/csv`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};

// INCI
export const suggestInci = (q) => api.get(`/inci/suggest`, { params: { q, limit: 10 } }).then((r) => r.data);

// Formulations
export const listFormulations = (params) => api.get("/formulations", { params }).then((r) => r.data);
export const getFormulation = (id) => api.get(`/formulations/${id}`).then((r) => r.data);
export const createFormulation = (payload) => api.post("/formulations", payload).then((r) => r.data);
export const updateFormulation = (id, payload) => api.put(`/formulations/${id}`, payload).then((r) => r.data);
export const deleteFormulation = (id) => api.delete(`/formulations/${id}`).then((r) => r.data);
export const formulationPdfUrl = (id) => `${API}/formulations/${id}/export/pdf`;

// Admin (pending deletions)
export const listPendingDeletions = () => api.get(`/storage/pending-deletions`).then((r) => r.data);
export const markDeletionProcessed = (eid) => api.delete(`/storage/pending-deletions/${eid}`).then((r) => r.data);
export const clearPendingDeletions = () => api.post(`/storage/pending-deletions/clear`).then((r) => r.data);
